import type { CSSProperties } from "react";
import { cn } from "@/lib/utils";
import type { Service } from "@/types/service";

interface ServicePreviewData {
  title?: string;
  description?: string;
  url?: string;
  text_color?: string;
  background_color?: string;
  accent_color?: string;
  logo_url?: string;
  banner_url?: string;
}

interface ServicePreviewProps {
  service: Service | ServicePreviewData;
  showHeader?: boolean;
}

// Mirrors the mobile (iOS / Android) landing page of app.appio.so. text_color
// and background_color override its --color and --bg-color; accent_color is
// not used there.
const DEFAULT_TEXT_COLOR = "rgb(29, 29, 31)";
const DEFAULT_BACKGROUND_COLOR = "#ffffff";
const LOGO_TILE_CLASSES =
  "relative z-10 mx-auto -mt-12 size-24 shrink-0 rounded-[22px] bg-(--preview-bg) p-3 shadow-[0_4px_16px_rgb(0_0_0/0.08)]";

export function ServicePreview({ service, showHeader }: ServicePreviewProps) {
  const colorVars = {
    "--preview-color": service.text_color || DEFAULT_TEXT_COLOR,
    "--preview-bg": service.background_color || DEFAULT_BACKGROUND_COLOR,
  } as CSSProperties;

  return (
    <div className="max-w-[360px] w-full">
      {showHeader && (
        <h3 className="mb-3 text-center text-[13px] font-medium text-muted-foreground">Preview</h3>
      )}
      <div className="overflow-hidden rounded-xl bg-card shadow-card">
        <div className="flex items-center gap-2 border-b bg-muted/50 px-4 py-3">
          <div className="flex gap-1.5">
            <div className="size-2.5 rounded-full bg-black/10" />
            <div className="size-2.5 rounded-full bg-black/10" />
            <div className="size-2.5 rounded-full bg-black/10" />
          </div>
        </div>

        <div
          className="relative flex aspect-[9/16] flex-col overflow-hidden bg-(--preview-bg) pb-[5em] text-center font-['SF_Pro_Text','SF_Pro_Icons',-apple-system,BlinkMacSystemFont,'Helvetica_Neue',Helvetica,Arial,sans-serif] text-base leading-[1.47059] tracking-[-0.022em] text-(--preview-color) antialiased"
          style={colorVars}
        >
          <div
            className="relative mx-[18px] mt-[18px] aspect-video min-h-16 shrink-[1000] rounded-[28px] bg-[color:color-mix(in_srgb,var(--preview-color)_4%,transparent)] bg-cover bg-center bg-no-repeat"
            style={service.banner_url ? { backgroundImage: `url(${service.banner_url})` } : undefined}
          >
            {!service.banner_url && (
              <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold uppercase tracking-wide text-[color:color-mix(in_srgb,var(--preview-color)_62%,transparent)]">
                Banner
              </span>
            )}
          </div>

          {/* Like app.appio.so, the img itself is the padded tile, so its 22px radius
              also rounds the image content (inner radius 22px minus 12px padding). */}
          {service.logo_url ? (
            <img
              src={service.logo_url}
              alt="Service logo"
              className={cn(LOGO_TILE_CLASSES, "block object-contain")}
            />
          ) : (
            <div className={cn(LOGO_TILE_CLASSES, "flex items-center justify-center")}>
              <span className="text-sm font-semibold text-[color:color-mix(in_srgb,var(--preview-color)_62%,transparent)]">
                LOGO
              </span>
            </div>
          )}

          <div className="flex min-h-0 flex-col px-[2em] pt-[1.5em]">
            <h2 className="shrink-0 break-words font-['SF_Pro_Display','SF_Pro_Icons',-apple-system,BlinkMacSystemFont,'Helvetica_Neue',Helvetica,Arial,sans-serif] text-[2em] font-semibold leading-[1.125] tracking-[-0.015em]">
              {service.title || "Title"}
            </h2>

            <p className="mx-auto mt-[0.6em] line-clamp-4 max-w-[30em] break-words text-[1.11765em] leading-[1.4211] tracking-[0.012em] text-[color:color-mix(in_srgb,var(--preview-color)_62%,transparent)]">
              {service.description || "Description"}
            </p>

            {/* Static mock, deliberately not interactive. */}
            <span className="mt-[2em] inline-block min-w-[14em] shrink-0 self-center rounded-full bg-(--preview-color) px-[2em] py-[0.9em] text-[1.05882em] font-semibold text-(--preview-bg) select-none cursor-not-allowed">
              Connect
            </span>
          </div>

          <footer className="absolute inset-x-0 bottom-0 px-[1em] py-[1.5em] text-[0.823529em] tracking-[-0.01em] text-[color:color-mix(in_srgb,var(--preview-color)_62%,transparent)]">
            Powered by Appio
          </footer>
        </div>
      </div>
    </div>
  );
}
